const TREND_LABELS = {
  insufficient: "Données insuffisantes",
  moderate: "En hausse modérée",
  stable: "Stable",
  strong: "En hausse forte",
};

const SUSPICION_LABELS = {
  backend: "Backend",
  disk: "Cache disque",
  frontend: "Frontend",
  insufficient: "Insuffisant",
  mixed: "Mixte",
};

const ANALYSIS_LIMITATIONS = Object.freeze([
  "Heuristiques déterministes basées sur le premier et le dernier snapshot disponibles, plus la continuité de croissance.",
  "Les timings et payloads récents dépendent des endpoints réellement touchés avant chaque snapshot.",
  "Une corrélation ne prouve pas seule la cause racine; le prompt demande ensuite une validation de cause probable et le plus petit correctif défendable.",
]);

const INTEGER_FORMATTER = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 0,
});

const DECIMAL_FORMATTER = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const PERCENT_FORMATTER = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  signDisplay: "always",
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "short",
  timeStyle: "short",
});

function normalizeText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function toFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function parseCapturedAt(snapshot) {
  const rawValue = normalizeText(snapshot?.captured_at);
  if (!rawValue) {
    return null;
  }

  const timestamp = Date.parse(rawValue);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return {
    raw: rawValue,
    timestamp,
  };
}

function sortSnapshotsAscending(snapshots) {
  return [...snapshots]
    .map((snapshot) => ({
      parsedCapturedAt: parseCapturedAt(snapshot),
      snapshot,
    }))
    .filter((entry) => entry.parsedCapturedAt)
    .sort((left, right) => left.parsedCapturedAt.timestamp - right.parsedCapturedAt.timestamp);
}

function formatDateTime(value) {
  const timestamp = Date.parse(String(value || ""));
  if (!Number.isFinite(timestamp)) {
    return "Date indisponible";
  }
  return DATE_TIME_FORMATTER.format(new Date(timestamp));
}

function formatInteger(value) {
  return INTEGER_FORMATTER.format(Math.round(Number(value) || 0));
}

function formatDecimal(value) {
  return DECIMAL_FORMATTER.format(Number(value) || 0);
}

function formatBytes(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "Donnée indisponible";
  }

  const absoluteValue = Math.abs(numericValue);
  if (absoluteValue < 1024) {
    return `${formatInteger(numericValue)} o`;
  }
  if (absoluteValue < 1024 ** 2) {
    return `${formatDecimal(numericValue / 1024)} Ko`;
  }
  if (absoluteValue < 1024 ** 3) {
    return `${formatDecimal(numericValue / (1024 ** 2))} Mo`;
  }
  return `${formatDecimal(numericValue / (1024 ** 3))} Go`;
}

function formatDurationMs(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "Donnée indisponible";
  }
  return `${formatDecimal(numericValue)} ms`;
}

function averageNumbers(values) {
  const numericValues = values
    .map((value) => toFiniteNumber(value))
    .filter((value) => value != null);
  if (!numericValues.length) {
    return null;
  }
  const total = numericValues.reduce((sum, value) => sum + value, 0);
  return total / numericValues.length;
}

function sumNumbers(values) {
  const numericValues = values
    .map((value) => toFiniteNumber(value))
    .filter((value) => value != null);
  if (!numericValues.length) {
    return null;
  }
  return numericValues.reduce((sum, value) => sum + value, 0);
}

function buildMetricSeries(sortedSnapshots, valueGetter) {
  return sortedSnapshots
    .map((entry) => ({
      capturedAt: entry.parsedCapturedAt.raw,
      value: toFiniteNumber(valueGetter(entry.snapshot)),
    }))
    .filter((entry) => entry.value != null);
}

function buildRuntimeAverageSeries(sortedSnapshots, fieldName) {
  return sortedSnapshots
    .map((entry) => {
      const recentProfiles = Array.isArray(entry.snapshot?.frontend?.runtime_requests?.recentEndpointProfiles)
        ? entry.snapshot.frontend.runtime_requests.recentEndpointProfiles
        : [];
      return {
        capturedAt: entry.parsedCapturedAt.raw,
        value: averageNumbers(recentProfiles.map((profile) => profile?.[fieldName])),
      };
    })
    .filter((entry) => entry.value != null);
}

function buildJsonRuntimeSizeSeries(sortedSnapshots) {
  return sortedSnapshots
    .map((entry) => {
      const files = Array.isArray(entry.snapshot?.disk_cache_state?.json_runtime_files)
        ? entry.snapshot.disk_cache_state.json_runtime_files
        : [];
      return {
        capturedAt: entry.parsedCapturedAt.raw,
        value: sumNumbers(files.map((item) => item?.size_bytes)),
      };
    })
    .filter((entry) => entry.value != null);
}

function resolveTrendKey(firstValue, lastValue, pointCount) {
  if (pointCount < 2 || firstValue == null || lastValue == null) {
    return "insufficient";
  }

  if (lastValue <= firstValue) {
    return "stable";
  }

  const baseline = Math.max(Math.abs(firstValue), 1);
  const deltaPct = ((lastValue - firstValue) / baseline) * 100;
  if (deltaPct >= 35) {
    return "strong";
  }
  if (deltaPct >= 10) {
    return "moderate";
  }
  return "stable";
}

function isContinuousGrowth(series) {
  if (series.length < 4) {
    return false;
  }

  for (let index = 1; index < series.length; index += 1) {
    if (series[index].value < series[index - 1].value) {
      return false;
    }
  }

  return series[series.length - 1].value > series[0].value;
}

function formatMetricValue(value, kind) {
  if (value == null) {
    return "Donnée indisponible";
  }

  switch (kind) {
    case "bytes":
      return formatBytes(value);
    case "duration_ms":
      return formatDurationMs(value);
    default:
      return formatInteger(value);
  }
}

function formatMetricDelta(deltaValue, kind) {
  if (deltaValue == null) {
    return "Comparaison indisponible";
  }

  const sign = deltaValue > 0 ? "+" : "";
  switch (kind) {
    case "bytes":
      return `${sign}${formatBytes(deltaValue)}`;
    case "duration_ms":
      return `${sign}${formatDurationMs(deltaValue)}`;
    default:
      return `${sign}${formatInteger(deltaValue)}`;
  }
}

function buildMetricSummary({
  key,
  kind = "count",
  label,
  series,
  note = "",
}) {
  const pointCount = series.length;
  const firstValue = pointCount ? series[0].value : null;
  const lastValue = pointCount ? series[pointCount - 1].value : null;
  const deltaValue = firstValue != null && lastValue != null ? lastValue - firstValue : null;
  const baseline = firstValue != null ? Math.max(Math.abs(firstValue), 1) : null;
  const deltaPct = baseline != null && deltaValue != null ? (deltaValue / baseline) * 100 : null;
  const trendKey = resolveTrendKey(firstValue, lastValue, pointCount);
  const continuousGrowth = isContinuousGrowth(series);
  const trendLabel = TREND_LABELS[trendKey];
  const growthSuffix = continuousGrowth ? " · croissance continue" : "";

  return {
    available: pointCount > 0,
    continuousGrowth,
    deltaPct,
    deltaPctLabel: deltaPct == null ? "Comparaison indisponible" : `${PERCENT_FORMATTER.format(deltaPct)} %`,
    deltaValue,
    deltaValueLabel: formatMetricDelta(deltaValue, kind),
    detail: firstValue == null || lastValue == null
      ? "Comparaison indisponible"
      : `${formatMetricValue(firstValue, kind)} -> ${formatMetricValue(lastValue, kind)} · ${formatMetricDelta(deltaValue, kind)} (${deltaPct == null ? "n/a" : `${PERCENT_FORMATTER.format(deltaPct)} %`})${growthSuffix}`,
    firstValue,
    firstValueLabel: formatMetricValue(firstValue, kind),
    key,
    kind,
    label,
    lastValue,
    lastValueLabel: formatMetricValue(lastValue, kind),
    note,
    pointCount,
    series,
    summary: firstValue == null || lastValue == null
      ? `${label}: données insuffisantes`
      : `${label}: ${formatMetricValue(firstValue, kind)} -> ${formatMetricValue(lastValue, kind)}`,
    trendKey,
    trendLabel,
  };
}

function getLoadedSectionCount(snapshot) {
  const explicitCount = toFiniteNumber(snapshot?.frontend?.data_manager?.loadedSectionCount);
  if (explicitCount != null) {
    return explicitCount;
  }
  const loadedSections = Array.isArray(snapshot?.frontend?.data_manager?.loadedSections)
    ? snapshot.frontend.data_manager.loadedSections
    : [];
  return loadedSections.length;
}

function getJsonRuntimeLastSnapshotSummary(snapshot) {
  const files = Array.isArray(snapshot?.disk_cache_state?.json_runtime_files)
    ? snapshot.disk_cache_state.json_runtime_files
    : [];
  const names = files
    .map((item) => normalizeText(item?.relative_path))
    .filter(Boolean)
    .slice(0, 3);
  return {
    fileCount: files.length,
    fileNames: names,
    totalSizeBytes: sumNumbers(files.map((item) => item?.size_bytes)) || 0,
  };
}

function calculateSuspicionScore(metric) {
  if (!metric || !metric.available) {
    return 0;
  }
  let score = 0;
  if (metric.trendKey === "strong") {
    score += 3;
  } else if (metric.trendKey === "moderate") {
    score += 2;
  }
  if (metric.continuousGrowth) {
    score += 1;
  }
  return score;
}

function resolveDominantSuspicion(metricsByKey) {
  const scoreByArea = {
    backend: 0,
    disk: 0,
    frontend: 0,
  };

  [
    metricsByKey.shellCacheKeys,
    metricsByKey.pageCacheKeys,
    metricsByKey.heapUsedBytes,
    metricsByKey.dataManagerInFlight,
  ].forEach((metric) => {
    scoreByArea.frontend += calculateSuspicionScore(metric);
  });

  [
    metricsByKey.runtimeDurationMs,
    metricsByKey.runtimeRunTotalMs,
    metricsByKey.runtimeSqlTotalMs,
    metricsByKey.responsePayloadBytes,
    metricsByKey.requestPayloadBytes,
  ].forEach((metric) => {
    scoreByArea.backend += calculateSuspicionScore(metric);
  });

  [
    metricsByKey.geocodeCacheBytes,
    metricsByKey.jsonRuntimeBytes,
  ].forEach((metric) => {
    scoreByArea.disk += calculateSuspicionScore(metric);
  });

  const rankedAreas = Object.entries(scoreByArea)
    .sort((left, right) => right[1] - left[1]);
  const [primaryArea, primaryScore] = rankedAreas[0] || ["insufficient", 0];
  const [, secondaryScore = 0] = rankedAreas[1] || [];

  if (primaryScore <= 0) {
    return {
      key: "insufficient",
      label: SUSPICION_LABELS.insufficient,
      reason: "Aucune dérive nette n'émerge des heuristiques disponibles.",
      scores: scoreByArea,
    };
  }

  if (primaryScore - secondaryScore < 2 && secondaryScore > 0) {
    return {
      key: "mixed",
      label: SUSPICION_LABELS.mixed,
      reason: "Plusieurs familles de métriques dérivent en parallèle sans écart net.",
      scores: scoreByArea,
    };
  }

  return {
    key: primaryArea,
    label: SUSPICION_LABELS[primaryArea] || SUSPICION_LABELS.insufficient,
    reason: primaryArea === "frontend"
      ? "Les caches frontend et/ou le heap montent plus vite que les timings backend récents."
      : primaryArea === "backend"
        ? "Les timings ou payloads backend récents dérivent plus que les métriques frontend." 
        : "Les artefacts disque montent plus vite que les compteurs runtime frontend/backend.",
    scores: scoreByArea,
  };
}

function buildGeocodeSummary(firstSnapshot, lastSnapshot, geocodeMetric) {
  const firstGeocode = firstSnapshot?.disk_cache_state?.geocode_cache || {};
  const lastGeocode = lastSnapshot?.disk_cache_state?.geocode_cache || {};
  if (lastGeocode.exists !== true) {
    return {
      isPertinent: false,
      line: "geocode_cache.db absent dans la fenêtre analysée.",
    };
  }

  const companionCount = Array.isArray(lastGeocode.companions) ? lastGeocode.companions.length : 0;
  const companionBytes = sumNumbers((lastGeocode.companions || []).map((item) => item?.size_bytes)) || 0;
  const rowCounts = lastGeocode.sqlite?.row_counts || {};
  const geocodeRowCount = toFiniteNumber(rowCounts.geocode_cache);
  const routeRowCount = toFiniteNumber(rowCounts.route_cache);
  const lastModifiedAt = formatDateTime(lastGeocode.modified_at);
  const rowSummary = [
    geocodeRowCount != null ? `${formatInteger(geocodeRowCount)} ligne(s) geocode_cache` : "",
    routeRowCount != null ? `${formatInteger(routeRowCount)} ligne(s) route_cache` : "",
  ].filter(Boolean).join(" · ");

  return {
    isPertinent: geocodeMetric.trendKey !== "insufficient" || companionCount > 0,
    line: [
      `geocode_cache.db: ${formatBytes(firstGeocode.size_bytes || 0)} -> ${formatBytes(lastGeocode.size_bytes || 0)} (${geocodeMetric.trendLabel.toLowerCase()})`,
      companionCount > 0 ? `${companionCount} fichier(s) compagnon(s), ${formatBytes(companionBytes)}` : "aucun fichier compagnon observé",
      rowSummary || "cardinalité SQLite indisponible",
      `mtime ${lastModifiedAt}`,
    ].join(" · "),
  };
}

function buildJsonRuntimeSummary(firstSnapshot, lastSnapshot, jsonRuntimeMetric) {
  const firstSummary = getJsonRuntimeLastSnapshotSummary(firstSnapshot);
  const lastSummary = getJsonRuntimeLastSnapshotSummary(lastSnapshot);
  if (!firstSummary.fileCount && !lastSummary.fileCount) {
    return {
      isPertinent: false,
      line: "Aucun JSON runtime connu observé dans la fenêtre analysée.",
    };
  }

  const fileNamesLabel = lastSummary.fileNames.length
    ? `Derniers fichiers: ${lastSummary.fileNames.join(", ")}`
    : "Noms de fichiers non disponibles";

  return {
    isPertinent: jsonRuntimeMetric.trendKey !== "insufficient" || lastSummary.fileCount > 0,
    line: [
      `JSON runtime connus: ${formatInteger(firstSummary.fileCount)} -> ${formatInteger(lastSummary.fileCount)} fichier(s)`,
      `${formatBytes(firstSummary.totalSizeBytes)} -> ${formatBytes(lastSummary.totalSizeBytes)} (${jsonRuntimeMetric.trendLabel.toLowerCase()})`,
      fileNamesLabel,
    ].join(" · "),
  };
}

function buildMetricCopyLine(metric) {
  return `${metric.label}: ${metric.firstValueLabel} -> ${metric.lastValueLabel} (${metric.deltaValueLabel}; ${metric.trendLabel.toLowerCase()}${metric.continuousGrowth ? ", croissance continue" : ""})`;
}

export function buildSystemDiagnosticsAnalysis(exportPayload = {}) {
  const sortedSnapshots = sortSnapshotsAscending(Array.isArray(exportPayload?.snapshots) ? exportPayload.snapshots : []);
  const totalSnapshotCount = toFiniteNumber(exportPayload?.total_snapshot_count) || sortedSnapshots.length;

  if (!sortedSnapshots.length) {
    return {
      analyzedSnapshotCount: 0,
      copilotPrompt: "",
      executiveSummaryLines: [
        "Aucun snapshot diagnostic disponible.",
        "Activez d'abord la collecte puis relancez l'analyse après quelques cycles.",
      ],
      keyMetrics: [],
      limitations: [...ANALYSIS_LIMITATIONS],
      periodLabel: "Aucune période disponible",
      stableMetrics: [],
      status: "empty",
      suspicion: {
        key: "insufficient",
        label: SUSPICION_LABELS.insufficient,
        reason: "Aucun snapshot disponible.",
        scores: {
          backend: 0,
          disk: 0,
          frontend: 0,
        },
      },
      suspectedDrifts: [],
      totalSnapshotCount,
    };
  }

  const firstSnapshot = sortedSnapshots[0].snapshot;
  const lastSnapshot = sortedSnapshots[sortedSnapshots.length - 1].snapshot;

  const metricsByKey = {
    shellCacheKeys: buildMetricSummary({
      key: "shell_cache_keys",
      label: "Clés cache shell",
      series: buildMetricSeries(sortedSnapshots, (snapshot) => snapshot?.frontend?.shell_cache?.cacheKeyCount),
    }),
    pageCacheKeys: buildMetricSummary({
      key: "page_cache_keys",
      label: "Clés cache page",
      series: buildMetricSeries(sortedSnapshots, (snapshot) => snapshot?.frontend?.page_cache?.cacheKeyCount),
    }),
    dataManagerLoadedSections: buildMetricSummary({
      key: "data_manager_loaded_sections",
      label: "Sections chargées Data Manager",
      series: buildMetricSeries(sortedSnapshots, getLoadedSectionCount),
      note: Array.isArray(lastSnapshot?.frontend?.data_manager?.loadedSections) && lastSnapshot.frontend.data_manager.loadedSections.length
        ? `Dernier snapshot: ${lastSnapshot.frontend.data_manager.loadedSections.join(", ")}`
        : "",
    }),
    dataManagerInFlight: buildMetricSummary({
      key: "data_manager_in_flight",
      label: "Actions Data Manager en vol",
      series: buildMetricSeries(sortedSnapshots, (snapshot) => snapshot?.frontend?.data_manager?.inFlightCount),
    }),
    runtimeDurationMs: buildMetricSummary({
      key: "runtime_duration_ms",
      kind: "duration_ms",
      label: "Durée moyenne des endpoints récents",
      series: buildRuntimeAverageSeries(sortedSnapshots, "durationMs"),
    }),
    runtimeRunTotalMs: buildMetricSummary({
      key: "runtime_run_total_ms",
      kind: "duration_ms",
      label: "run_total moyen backend récent",
      series: buildRuntimeAverageSeries(sortedSnapshots, "runTotalMs"),
    }),
    runtimeSqlTotalMs: buildMetricSummary({
      key: "runtime_sql_total_ms",
      kind: "duration_ms",
      label: "sql_total moyen backend récent",
      series: buildRuntimeAverageSeries(sortedSnapshots, "sqlTotalMs"),
    }),
    requestPayloadBytes: buildMetricSummary({
      key: "request_payload_bytes",
      kind: "bytes",
      label: "Payload requête moyen récent",
      series: buildRuntimeAverageSeries(sortedSnapshots, "requestBodyBytes"),
    }),
    responsePayloadBytes: buildMetricSummary({
      key: "response_payload_bytes",
      kind: "bytes",
      label: "Payload réponse moyen récent",
      series: buildRuntimeAverageSeries(sortedSnapshots, "responseBodyBytes"),
    }),
    heapUsedBytes: buildMetricSummary({
      key: "heap_used_bytes",
      kind: "bytes",
      label: "Heap JS utilisé",
      series: buildMetricSeries(sortedSnapshots, (snapshot) => snapshot?.frontend?.performance_memory?.usedJSHeapSize),
    }),
    geocodeCacheBytes: buildMetricSummary({
      key: "geocode_cache_bytes",
      kind: "bytes",
      label: "Taille geocode_cache.db",
      series: buildMetricSeries(sortedSnapshots, (snapshot) => snapshot?.disk_cache_state?.geocode_cache?.size_bytes),
    }),
    jsonRuntimeBytes: buildMetricSummary({
      key: "json_runtime_bytes",
      kind: "bytes",
      label: "Taille totale des JSON runtime connus",
      series: buildJsonRuntimeSizeSeries(sortedSnapshots),
    }),
  };

  const orderedMetrics = [
    metricsByKey.shellCacheKeys,
    metricsByKey.pageCacheKeys,
    metricsByKey.dataManagerLoadedSections,
    metricsByKey.dataManagerInFlight,
    metricsByKey.runtimeDurationMs,
    metricsByKey.runtimeRunTotalMs,
    metricsByKey.runtimeSqlTotalMs,
    metricsByKey.requestPayloadBytes,
    metricsByKey.responsePayloadBytes,
    metricsByKey.heapUsedBytes,
    metricsByKey.geocodeCacheBytes,
    metricsByKey.jsonRuntimeBytes,
  ].filter((metric) => metric.available);

  const suspicion = resolveDominantSuspicion(metricsByKey);
  const geocodeSummary = buildGeocodeSummary(firstSnapshot, lastSnapshot, metricsByKey.geocodeCacheBytes);
  const jsonRuntimeSummary = buildJsonRuntimeSummary(firstSnapshot, lastSnapshot, metricsByKey.jsonRuntimeBytes);
  const suspectedDrifts = orderedMetrics
    .filter((metric) => metric.trendKey === "strong" || metric.trendKey === "moderate")
    .map(buildMetricCopyLine);
  const stableMetrics = orderedMetrics
    .filter((metric) => metric.trendKey === "stable")
    .map(buildMetricCopyLine)
    .slice(0, 5);
  const periodLabel = `${formatDateTime(sortedSnapshots[0].parsedCapturedAt.raw)} -> ${formatDateTime(sortedSnapshots[sortedSnapshots.length - 1].parsedCapturedAt.raw)}`;
  const truncationLine = totalSnapshotCount > sortedSnapshots.length
    ? `Fenêtre partielle: ${formatInteger(sortedSnapshots.length)} snapshot(s) les plus récents analysés sur ${formatInteger(totalSnapshotCount)} disponibles.`
    : `Fenêtre complète: ${formatInteger(sortedSnapshots.length)} snapshot(s) analysés.`;

  const executiveSummaryLines = [
    `Plage analysée: ${periodLabel}.`,
    `${formatInteger(sortedSnapshots.length)} snapshot(s) lus sur ${formatInteger(totalSnapshotCount)} disponible(s). ${truncationLine}`,
    suspectedDrifts.length
      ? `Principales métriques en hausse: ${suspectedDrifts.slice(0, 4).join(" | ")}.`
      : "Aucune hausse nette détectée sur les métriques disponibles.",
    stableMetrics.length
      ? `Principales métriques stables: ${stableMetrics.slice(0, 3).join(" | ")}.`
      : "Peu de métriques stables exploitables sur la fenêtre analysée.",
    `Suspicion dominante: ${suspicion.label}. ${suspicion.reason}`,
    geocodeSummary.line,
    jsonRuntimeSummary.line,
    `Limites: ${ANALYSIS_LIMITATIONS.join(" ")}`,
  ];

  const promptSections = [
    "Contexte technique",
    "- SaaS de gestion de flotte de camions.",
    "- Frontend React/Vite, backend FastAPI, PostgreSQL.",
    "- Diagnostic performance activé sur plusieurs jours avec snapshots horodatés déjà collectés.",
    "",
    "Fenêtre analysée",
    `- Période: ${periodLabel}.`,
    `- Snapshots lus: ${formatInteger(sortedSnapshots.length)} sur ${formatInteger(totalSnapshotCount)} disponibles.`,
    totalSnapshotCount > sortedSnapshots.length ? `- Note: ${truncationLine}` : "- Note: fenêtre complète disponible sur la période analysée.",
    "",
    "Résumé des dérives observées",
    suspectedDrifts.length
      ? suspectedDrifts.map((line) => `- ${line}`).join("\n")
      : "- Aucune dérive forte détectée; les métriques visibles restent globalement stables.",
    "",
    "Métriques restées stables",
    stableMetrics.length
      ? stableMetrics.map((line) => `- ${line}`).join("\n")
      : "- Aucune stabilité nette exploitable n'a été détectée sur au moins deux snapshots comparables.",
    "",
    "Caches disque et runtime",
    `- ${geocodeSummary.line}`,
    `- ${jsonRuntimeSummary.line}`,
    "",
    "Verdict heuristique actuel",
    `- Suspicion dominante: ${suspicion.label}.`,
    `- Raison: ${suspicion.reason}`,
    "",
    "Mission demandée",
    "- Analyse la cause racine probable de cette dérive.",
    "- Classe les suspects par ordre de probabilité: frontend / backend / cache disque.",
    "- Propose le plus petit correctif défendable en premier.",
    "- Évite les gros refactors non justifiés.",
    "- Indique les vérifications minimales à lancer ensuite pour confirmer l'hypothèse.",
  ];

  return {
    analyzedSnapshotCount: sortedSnapshots.length,
    copilotPrompt: promptSections.join("\n"),
    executiveSummaryLines,
    keyMetrics: orderedMetrics,
    limitations: [...ANALYSIS_LIMITATIONS],
    periodLabel,
    stableMetrics,
    status: "ready",
    suspicion,
    suspectedDrifts,
    totalSnapshotCount,
  };
}
